import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Room, Participant, PlaylistItem, ParticipantRole } from '../types';

interface RoomState {
  // Room data
  room: Room | null;
  participants: Participant[];
  playlist: PlaylistItem[];
  currentIndex: number;
  myRole: ParticipantRole | null;

  // Connection state
  isConnected: boolean;

  // Actions
  setRoom: (room: Room) => void;
  setParticipants: (participants: Participant[]) => void;
  setPlaylist: (playlist: PlaylistItem[]) => void;
  setCurrentIndex: (index: number) => void;
  setMyRole: (role: ParticipantRole) => void;
  setConnected: (connected: boolean) => void;
  addParticipant: (participant: Participant) => void;
  removeParticipant: (userId: string) => void;
  updateParticipantRole: (userId: string, role: ParticipantRole) => void;
  reset: () => void;
}

const initialState = {
  room: null,
  participants: [],
  playlist: [],
  currentIndex: 0,
  myRole: null,
  isConnected: false,
};

export const useRoomStore = create<RoomState>()(
  persist(
    (set) => ({
      ...initialState,

      setRoom: (room) => set({ room }),

      setParticipants: (participants) => set({ participants }),

      setPlaylist: (playlist) => set({ playlist }),

      setCurrentIndex: (index) => set({ currentIndex: index }),

      setMyRole: (role) => set({ myRole: role }),

      setConnected: (connected) => set({ isConnected: connected }),

      addParticipant: (participant) =>
        set((state) => ({
          participants: [...state.participants, participant],
        })),

      removeParticipant: (userId) =>
        set((state) => ({
          participants: state.participants.filter((p) => p.userId !== userId),
        })),

      updateParticipantRole: (userId, role) =>
        set((state) => ({
          participants: state.participants.map((p) =>
            p.userId === userId ? { ...p, role } : p
          ),
        })),

      reset: () => set(initialState),
    }),
    {
      name: 'yt-music-sync-room',
      partialize: (state) => ({
        room: state.room,
        playlist: state.playlist,
        currentIndex: state.currentIndex,
        myRole: state.myRole,
        // Don't persist participants (they might be outdated) or isConnected
      }),
    }
  )
);
